import * as moment from 'moment-timezone';

import { Injectable } from '@angular/core';
import { AuthService } from '../api/auth.service';
import { BehaviorSubject } from 'rxjs';
import { LoginGuardService } from '../api/login-guard.service';
import { RoutesService } from '../api/routes.service';
import { Router } from '@angular/router';
import { AccountType } from '../constants/account-types';
import { LangService } from '../core/lang.service';
import { TimezoneService } from '../core/timezone.service';
import { EDeliveryOption } from '../api/models/db/test-sessions.schema';
import { ITestSessionAvailTT, ITestSessionAvailTTExt } from '../api/models/db/test-session-avail-tt.schema';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Location } from '@angular/common';

export interface IMyApplAcct {
  uid: number,
  name: string,
  capId: string,
}
enum InvigLang {
  FRENCH_INVIG = 'French Invigilation',
  ENGLISH_INVIG = 'English Invigilation',
  BILINGUAL_INVIG = 'Bilingual Invigilation (English and French) '
}

export interface ISessionTT {
  m: moment.Moment;
  id:number,
  sessionNum:number,
  instGroupId:number,
  testWindowTitle: string,
  dateDisplay?: string,
  timeFrom: string,
  institutionName?: string,
  testLocation?: string,
  hasAccessCode: boolean, 
  status: string,
  timeZone: string,
  isByod: boolean,
  isFull: boolean,
  isBookable?: boolean,
  isExpired?: boolean,
  isFilteredOut?:boolean,
  test_attempt_id?:number,
  delivery_format?:string,
  videostream_link?:string,
  videostream_password?:string,
  is_seb_disabled?:boolean,
  invigLang?:string,
  ts_invigLang?:string,
  //
  videostreamSafeUrl?:SafeResourceUrl, // tacked on in the client
}

@Injectable({
  providedIn: 'root'
})
export class MyApplAccountService {

  constructor(
    private auth: AuthService,
    private loginGuard: LoginGuardService,
    private routes: RoutesService,
    private router: Router,
    private location: Location,
    private lang: LangService,
    private timezone: TimezoneService,
  ) { 
    this.init();
  }

  public isMakingApiReq:boolean = false;

  private info:BehaviorSubject<IMyApplAcct> = new BehaviorSubject(null);

  private init(){
    this.auth.user().subscribe(userInfo => {
      if(userInfo){
        this.info.next({
          uid: userInfo.uid,
          name: userInfo.firstName + ' ' + userInfo.lastName,
          capId: '224466',
        });
      }
      else{
        this.info.next(null);
      }
    })
  }

  public sub(){
    return this.info;
  }

  public getDisplayName(){
    return this.info.getValue().name
  }

  public async sendStripeSessionForAppeal() {
    const lang_code = this.lang.c();
    const currentDomain = window.location.href;
    let domainSplit = currentDomain.split('/');
    const attemptId = parseInt(domainSplit[domainSplit.length - 1]);
    const type = 'APPEAL';
    const body = {type, attemptId, lang_code};
    const stripeCheckout = await this.auth.apiCreate(
      this.routes.TEST_TAKER_TEST_SESSIONS_STRIPE_CHECKOUT,
      body
    );
    location.assign(stripeCheckout.url);
  }

  public async sendStripeSessionForCreditPurchase(test_session_id: number, access_code: string, lang: string, isAccommReq: boolean) {
    const lang_code = lang;
    const isAccomm = isAccommReq ? 1 : 0;
    const type = 'CREDIT_PURCHASE';
    const body = {test_session_id, type, lang_code, isAccomm}
    const stripeCheckout = await this.auth.apiCreate(
      this.routes.TEST_TAKER_TEST_SESSIONS_STRIPE_CHECKOUT,
      body
    );
    location.assign(stripeCheckout.url);
  }
  
  bookSession(test_session_id:number, access_code: string, paymentsEnabled?: boolean){
    if (!this.isMakingApiReq) {
      this.isMakingApiReq = true;
      return this.auth.apiCreate(
        this.routes.TEST_TAKER_TEST_SESSIONS_BOOKING,
        { test_session_id, access_code, lang: this.lang.c() }
      )
      .then((res) => {
        this.isMakingApiReq = false;
      })
      .catch( e => {
        if (e.message === 'INVALID_ACCESS_CODE'){
          const caption = 'msg_invalid_code'
          this.loginGuard.confirmationReqActivate({caption, confirm: ()=>{} })
        }
        else if (e.message === 'TEST_SESSION_FULL'){
          const caption = this.lang.tra('msg_session_full');
          this.loginGuard.confirmationReqActivate({caption, confirm: ()=>{} })
        }
        else if (e.message === 'TEST_SESSION_BUFFER_CLOSED'){
          const caption = 'This session is closed for bookings. Please contact the test centre if you believe that the bookings have been erroneously closed early.';
          this.loginGuard.confirmationReqActivate({caption, confirm: ()=>{} })
        }
        else if (e.message === 'EXISTING_BOOKING'){
          const caption = 'msg_reg_error_completed';
          this.loginGuard.confirmationReqActivate({caption, confirm: ()=>{} })
        }
        else if (e.message === 'TEST_COMPLETED'){
          const caption = 'msg_reg_error_completed';
          this.loginGuard.confirmationReqActivate({caption, confirm: ()=>{} })
        }
        else if (e.message === 'MPT_STATUS_NOT_ELIGIBLE'){
          const caption = 'notif_restricted_booking_simple';
          this.loginGuard.confirmationReqActivate({caption, confirm: ()=>{} })
        }
        throw e;
      }) 
    }
    return Promise.resolve();
  }

  waitlistSession(test_session_id:number, access_code: string){
    if (!this.isMakingApiReq) {
      this.isMakingApiReq = true;
      return this.auth.apiCreate(
        this.routes.TEST_TAKER_TEST_SESSIONS_WAITLIST,
        { test_session_id, access_code, lang: this.lang.c()}
      )
      .then( () => {
        this.isMakingApiReq = false;
        this.gotoDashboard()
      })
      .catch( e => {
        if (e.message === 'INVALID_ACCESS_CODE'){
          const caption = 'You have provided an invalid access code for this test session.'
          this.loginGuard.confirmationReqActivate({caption, confirm: ()=>{} })
        }
        else if (e.message === 'MAXIMUM_WAITLIST_REACHED'){
          console.log(e.message)
          const caption = 'msg_reg_error_max_wait';
          this.loginGuard.confirmationReqActivate({caption, confirm: ()=>{} })
        }
        else{
          const caption = this.lang.tra('msg_already_registered');
          this.loginGuard.confirmationReqActivate({caption, confirm: this.gotoDashboard })
        }
        throw e;
      })
    }
    return Promise.resolve();
  }

  gotoDashboard = () => {
    this.router.navigate([`/${this.lang.c()}/${AccountType.TEST_TAKER}/dashboard`]);
  }

  checkIfCreditsEnabled = () => {
    return this.auth.apiFind(this.routes.CREDITS_CREDIT_SYSTEM);
  }

  getUserCreditInfo = () => {
    return this.auth.apiFind(this.routes.TEST_TAKER_CREDIT_DETAILS);
  }

  checkIfPaymentsEnabled = () => {
    return this.auth.apiFind(this.routes.CREDITS_PAYMENT_SYSTEM);
  }

  sanitizeSessionInfo(session:ITestSessionAvailTTExt, institutionName:string = '') : ISessionTT {
    const m = this.timezone.moment(session.date_time_start);
    // '3:00pm, Sept. 16, 2020 (Wednesday)'
    const FMT_DATE_DAY_DISPLAY = this.lang.tra('datefmt_day_month_year_dow');
    const FMT_DATE_TIME_DISPLAY = this.lang.tra('datefmt_time');
    let testWindowTitle;
    try {
      const titles = JSON.parse(session.test_window_title);
      testWindowTitle = titles[this.lang.c()];
    }
    catch(e){}

    let isBookable = true;
    if (session.pending !== undefined) {
      isBookable = session.pending === 0 ? true : false;
    }
    return {
        m,
        id:session.id,
        delivery_format: session.delivery_format,
        videostream_link: session.videostream_link,
        videostream_password: session.videostream_password,
        sessionNum:0,
        instGroupId:session.instit_group_id,
        dateDisplay: m.format(FMT_DATE_DAY_DISPLAY),
        timeFrom: m.format(FMT_DATE_TIME_DISPLAY),
        timeZone: this.timezone.getTimezone(),
        institutionName,
        testWindowTitle,
        status: session.status,
        is_seb_disabled: session.is_seb_disabled == 1 ? true: false,
        testLocation: [
          `${this.lang.tra('cts_location_room_lbl')} ${this.renderRoom(session)}, ${session.campus_building}`,
          `${session.address}`,
          `${session.city} ${session.province} ${session.postal_code}`,
          `${session.phone_number}`,
        ].join('\n'),
        hasAccessCode: session.is_access_code_enabled === 1, 
        isByod: session.delivery_format === EDeliveryOption.BYOD,
        isFull: session.booked ? session.booked >= session.capacity : false,
        isBookable: isBookable,
        invigLang: this.getInvigLangSlug(session.invigLang) || this.getInvigLangSlug(session.ts_invigLang),
    }
  }

  private getInvigLangSlug(lang:string){
    if(lang == InvigLang.ENGLISH_INVIG) return this.lang.tra('lbl_en');
    if(lang == InvigLang.FRENCH_INVIG) return this.lang.tra('lbl_fr');
    if(lang == InvigLang.BILINGUAL_INVIG) return this.lang.tra('lbl_bil')

  }
  
  private renderRoom(session: ITestSessionAvailTT) {
    let room: string;
    if (session.delivery_format === 'remote') {
      room = this.lang.tra('tra_remote');
    } else {
      room = session.room;
    }
    return room;
  }

}
